import { Box, Checkbox, FormControlLabel } from '@mui/material';
import React, { useCallback } from 'react';
import type { ActorInfo } from 'sr-types/lib/search/v1/graphql';
import LexicaljsEditor from '../../lexical/LexicaljsEditor';
import { accountEntityReferenceAutocompletePersonOnlyFilter } from '../../../person/person';
import ContactFormWidget from '../../../production/resource/orders/editor/resourceOrderApprovalsStep/ContactFormWidget';
import { ENTITY_CONSTANTS } from '../../constants/entityConstants';
import { useI18n } from '../../i18n/I18n';

export type NotificationFormWidgetValue = {
    notify?: boolean;
    customMessage?: string;
    notifyContacts?: ActorInfo[];
};

export interface NotificationFormWidgetProps {
    value: NotificationFormWidgetValue;
    onChange: (value: NotificationFormWidgetValue) => void;
    hideNotifyContactsSelection?: boolean;
    allowAddingNotifyContactsOptions?: boolean;
    editorId?: string;
}

export default function NotificationFormWidget({
    value,
    onChange,
    hideNotifyContactsSelection = false,
    allowAddingNotifyContactsOptions = false,
    editorId = 'notification-custom-message'
}: NotificationFormWidgetProps) {
    const notifyPeopleLabel = useI18n('common.notify.people');
    const contentPlaceholder = useI18n('share.notify.customMessage.placeholder');
    const isNotifyEnabled = value.notify ?? true;

    const handleNotifyChange = useCallback(
        (_e: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
            onChange({
                ...value,
                notify: checked
            });
        },
        [onChange, value]
    );

    const handleNotifyContactsChange = useCallback(
        (_name: string, selectedContacts: ActorInfo[]) => {
            onChange({
                ...value,
                notifyContacts: selectedContacts ?? []
            });
        },
        [onChange, value]
    );

    const handleCustomMessageChange = useCallback(
        (content: string) => {
            onChange({
                ...value,
                customMessage: content
            });
        },
        [onChange, value]
    );

    return (
        <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FormControlLabel
                sx={{ margin: 0 }}
                control={<Checkbox checked={isNotifyEnabled} onChange={handleNotifyChange} />}
                label={notifyPeopleLabel}
            />
            {isNotifyEnabled && (
                <>
                    {!hideNotifyContactsSelection && (
                        <ContactFormWidget
                            name="notifyContacts"
                            labelToken="common.search.for.people"
                            filter={accountEntityReferenceAutocompletePersonOnlyFilter}
                            entity={ENTITY_CONSTANTS.account}
                            showEmail
                            addOption={allowAddingNotifyContactsOptions}
                            value={value.notifyContacts ?? []}
                            onChange={handleNotifyContactsChange}
                        />
                    )}
                    <LexicaljsEditor
                        id={editorId}
                        isSmall={true}
                        contentPlaceholder={contentPlaceholder}
                        sx={{ '--lexical-placeholder-top': '54px' }}
                        readOnly={false}
                        clearOnSave={false}
                        clearOnCancel
                        content={value.customMessage ?? ''}
                        onChange={handleCustomMessageChange}
                        shouldAutoFocus
                    />
                </>
            )}
        </Box>
    );
}
